import pica from "pica";
import { readFileToDataUrl } from "./file";
import { canvasToBlob } from "./canvas";

const DEFAULT_AVATAR_JPEG_QUALITY = 0.85;
const MIN_AVATAR_JPEG_QUALITY = 0.5;

export type NormalizeImageFileOptions = {
  maxSizeBytes: number;
  maxDimension?: number;
  mimeType?: string;
};

export async function normalizeImageFile(
  file: File,
  options: NormalizeImageFileOptions
): Promise<File> {
  const maxDimension: number = options.maxDimension ?? 1024;
  const mimeType: string = options.mimeType ?? "image/jpeg";
  const bitmap: ImageBitmap = await loadOrientedImageBitmap(file);
  const scale: number = Math.min(
    1,
    maxDimension / Math.max(bitmap.width, bitmap.height)
  );
  const width: number = Math.max(1, Math.round(bitmap.width * scale));
  const height: number = Math.max(1, Math.round(bitmap.height * scale));
  const canvas: HTMLCanvasElement = document.createElement("canvas");

  canvas.width = width;
  canvas.height = height;

  const context: CanvasRenderingContext2D | null = canvas.getContext("2d");

  if (!context) {
    bitmap.close();

    throw new Error("canvas-2d-unavailable");
  }

  context.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  let quality: number = DEFAULT_AVATAR_JPEG_QUALITY;
  let blob: Blob = await canvasToBlob(canvas, mimeType, quality);

  while (
    blob.size > options.maxSizeBytes &&
    quality > MIN_AVATAR_JPEG_QUALITY
  ) {
    quality -= 0.1;
    blob = await canvasToBlob(canvas, mimeType, quality);
  }

  if (blob.size > options.maxSizeBytes) {
    throw new Error("image-normalize-size-exceeded");
  }

  const baseName: string = file.name.replace(/\.[^.]+$/, "") || "avatar";

  return new File([blob], `${baseName}.jpg`, {
    type: mimeType,
    lastModified: Date.now()
  });
}

async function loadOrientedImageBitmap(file: File): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    return createImageBitmap(file);
  }
}

export function scaleImage(input: Blob): Promise<string> {
  return new Promise(async (res, rej) => {
    const dataUrl = await readFileToDataUrl(input);

    const image = new Image();
    image.onload = () => {
      const [newWidth, newHeight] = ff(
        image.height,
        image.width,
        256 / Math.max(image.width, image.height)
      );

      const canvas = document.createElement("canvas");
      canvas.width = newWidth;
      canvas.height = newHeight;

      const picaInstance = pica();
      picaInstance.resize(image, canvas).then((resized) => {
        const dataUrl = resized.toDataURL();
        res(dataUrl);
      });
    };
    image.src = dataUrl;
  });
}

function ff(
  height: number,
  width: number,
  percentage: number
): [number, number] {
  const min = Math.min(width, height);
  const result = Math.max(min * (percentage / 100), 350);
  const newHeight = (result / min) * height;
  const newWidth = (result / min) * width;
  return [newWidth, newHeight];
}
